import React from 'react';
import { eachDayOfInterval, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, startOfMonth, startOfWeek } from 'date-fns';
import { Booking, Settings } from '../../types/booking';
import { dayIsOpen } from '../../utils/availability';
import { dayStats, statusAccent } from '../../utils/agenda';
import { euro } from '../../utils/format';
type Props = {
  month: Date;
  bookings: Booking[];
  settings: Settings;
  selected: string;
  onSelectDate: (iso: string) => void;
  onOpenBooking: (booking: Booking) => void;
};
const labels = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
export function AgendaMonth({
  month,
  bookings,
  settings,
  selected,
  onSelectDate,
  onOpenBooking
}: Props) {
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(month), {
      weekStartsOn: 1
    }),
    end: endOfWeek(endOfMonth(month), {
      weekStartsOn: 1
    })
  });
  return <div className="overflow-hidden rounded-2xl border border-edge bg-carbon" data-id="element-148" data-mp="3o0hq2witn0yu">
      <div className="grid grid-cols-7 border-b border-edge text-center text-[11px] uppercase tracking-[0.14em] text-dim" data-id="element-149" data-mp="3nyf2sk91zlxt">
        {labels.map(day => <span key={day} className="py-2.5" data-id="element-150" data-mp="9k65dox758os">
            {day}
          </span>)}
      </div>

      <div className="grid grid-cols-7" data-id="element-151" data-mp="eczcgjg50t41">
        {days.map(day => {
        const iso = format(day, 'yyyy-MM-dd');
        const outside = !isSameMonth(day, month);
        const open = dayIsOpen(iso, settings);
        const blocked = settings.blockedDates.includes(iso);
        const stats = dayStats(bookings, iso);
        const dayBookings = bookings.filter(b => b.date === iso && b.status !== 'cancelled').sort((a, b) => a.time < b.time ? -1 : 1);
        const isSelected = iso === selected;
        const today = isSameDay(day, new Date());
        return <div key={iso} className={`relative min-h-[62px] border-b border-r border-edge p-1.5 last:border-r-0 sm:min-h-[104px] sm:p-2 lg:min-h-[132px] ${outside ? 'bg-void/40' : blocked ? 'bg-ember/[0.06]' : !open ? 'bg-void/60' : ''} ${isSelected ? 'ring-1 ring-inset ring-ember' : ''}`} data-id="element-152" data-mp="1703hgof8d7ft">
              <button type="button" onClick={() => onSelectDate(iso)} className="flex w-full items-center justify-between gap-2 text-left" data-id="element-153" data-mp="3h3ss31c63ljv">
                <span className={`flex h-7 w-7 items-center justify-center rounded-full text-sm ${today ? 'bg-ember font-semibold text-void' : outside ? 'text-dim/35' : 'text-chalk hover:text-ember'}`} data-id="element-154" data-mp="2pbjutv8j6p68">
                  {format(day, 'd')}
                </span>
                {stats.count > 0 && <span className="hidden text-[10px] uppercase tracking-[0.1em] text-dim sm:inline" data-id="element-155" data-mp="21mr1m6jamvfb">
                    {stats.count}× · {euro(stats.revenue)}
                  </span>}
              </button>

              {!outside && <div className="mt-1 flex items-center gap-1 sm:hidden" data-id="element-156" data-mp="1wb3mzr22q4iu">
                  {stats.count > 0 ? <>
                      <span className="h-1.5 w-1.5 rounded-full bg-ember" aria-hidden="true" data-id="element-157" data-mp="1mjqz1ajebyi3" />
                      <span className="text-[10px] text-dim" data-id="element-158" data-mp="2a9bqyixj263i">{stats.count}</span>
                    </> : blocked ? <span className="text-[10px] text-ember" data-id="element-159" data-mp="2f7da13uye5ez">blok</span> : !open ? <span className="text-[10px] text-dim/50" data-id="element-160" data-mp="20a467aogr96d">—</span> : null}
                </div>}

              {!outside && <div className="mt-1.5 hidden space-y-1 sm:block" data-id="element-161" data-mp="ow8phxhwusd1">
                  {!open && !blocked && <span className="block text-[10px] uppercase tracking-[0.12em] text-dim/60" data-id="element-162" data-mp="tvlbkg0yjvek">Gesloten</span>}
                  {blocked && <span className="block text-[10px] uppercase tracking-[0.12em] text-ember" data-id="element-163" data-mp="1a418geyto1vs">Geblokkeerd</span>}

                  {dayBookings.slice(0, 2).map(booking => <button key={booking.id} type="button" onClick={() => onOpenBooking(booking)} className={`block w-full truncate rounded-md border px-1.5 py-1 text-left text-[11px] transition-colors duration-200 ease-smooth hover:border-ember ${statusAccent(booking)}`} data-id="element-164" data-mp="10aajza2p3rg3">
                      {booking.time} {booking.customer.name}
                    </button>)}

                  {dayBookings.length > 2 && <button type="button" onClick={() => onSelectDate(iso)} className="block text-[10px] text-dim transition-colors duration-200 ease-smooth hover:text-ember" data-id="element-165" data-mp="7lmvz6qkanyu">
                      +{dayBookings.length - 2} meer
                    </button>}

                  {open && !blocked && dayBookings.length === 0 && <span className="block text-[10px] uppercase tracking-[0.12em] text-dim/45" data-id="element-166" data-mp="2t0mh05s57v0o">Vrij</span>}
                </div>}
            </div>;
      })}
      </div>
    </div>;
}